import { ToastContent, ToastSessionState } from 'types/embeddedPayButtonTypes';

// Constants for storage and timing
const STORAGE_PREFIX = 'embedded_';
const TOAST_EXPIRATION_HOURS = 24;
const TOAST_SHOW_DELAY = 1000; // Delay before showing persisted toast

// Simple sessionStorage wrapper for the embedded button context
const embeddedSessionStorage = {
  get: (key: string): any => {
    try {
      const item = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  },
  set: (key: string, value: any): void => {
    try {
      sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(value));
    } catch {
      // Ignore storage errors
    }
  },
  remove: (key: string): void => {
    try {
      sessionStorage.removeItem(`${STORAGE_PREFIX}${key}`);
    } catch {
      // Ignore storage errors
    }
  },
  clearAll: (): void => {
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(STORAGE_PREFIX)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch {
      // Ignore storage errors
    }
  },
};

(async function (window: Window, document: Document) {
  let buttonEls: HTMLButtonElement[] = Array.from(
    document.getElementsByClassName('embedded-pay-button'),
  ) as HTMLButtonElement[];
  let iframeLoading = false;
  let host = '';
  let loaderEl: HTMLElement | null = null;
  let toastEl: HTMLElement | null = null;
  let modalEl: HTMLElement | null = null;
  let toastSessionState: ToastSessionState = 'fresh';
  let lastClosedFormId: string | null = null; // Track the last closed form instance

  // Track which buttons we've already initialized to avoid duplicates
  const initializedButtons = new WeakSet<HTMLButtonElement>();
  let restoreCartAbandonment = false;

  function init() {
    // Create modal elements only once
    if (!modalEl) {
      modalEl = createModalEl();
    }
    if (!loaderEl) {
      loaderEl = createLoaderEl();
    }
    if (!toastEl) {
      toastEl = createToastEl();
    }

    // Check for persisted toast state on initialization
    const persistedToastState = embeddedSessionStorage.get('toastState');
    const persistedToastContent = embeddedSessionStorage.get('toastContent');
    const persistedHost = embeddedSessionStorage.get('hostUrl');

    if (
      persistedToastState &&
      persistedToastContent &&
      persistedHost === window.location.href
    ) {
      // Check if session is expired (24 hours)
      const lastUpdated = embeddedSessionStorage.get('lastUpdated');
      const hoursSinceUpdate = lastUpdated
        ? (Date.now() - lastUpdated) / (1000 * 60 * 60)
        : TOAST_EXPIRATION_HOURS + 1;

      if (
        hoursSinceUpdate <= TOAST_EXPIRATION_HOURS &&
        persistedToastState === 'toastShown'
      ) {
        toastSessionState = persistedToastState;
        // Show the persisted toast
        setTimeout(() => {
          showToast(persistedToastContent);
        }, TOAST_SHOW_DELAY); // Small delay to ensure DOM is ready
      } else {
        // Clear expired or invalid session data
        embeddedSessionStorage.clearAll();
      }
    }

    // Get fresh list of buttons (in case new ones were added)
    buttonEls = Array.from(
      document.getElementsByClassName('embedded-pay-button'),
    ) as HTMLButtonElement[];

    let hasValidHost = false;
    let newButtonsInitialized = 0;

    for (let i = 0; i < buttonEls.length; i++) {
      let buttonEl = buttonEls[i];

      // Skip if already initialized
      if (initializedButtons.has(buttonEl)) {
        continue;
      }

      const dataHost = buttonEl.getAttribute('data-host');

      if (!dataHost) {
        buttonEl.disabled = true;
        initializedButtons.add(buttonEl);
        continue;
      }

      try {
        const url = new URL(dataHost);
        const existingParams = new URLSearchParams(url.search);
        url.searchParams.set('embedded', 'true');
        url.searchParams.set('parentUrl', window.location.href);

        // Preserve any parameters that were already set (like restoreCartAbandonment)
        existingParams.forEach((value, key) => {
          if (key !== 'embedded' && key !== 'parentUrl') {
            url.searchParams.set(key, value);
          }
        });

        host = url.toString();
        hasValidHost = true;
        newButtonsInitialized++;
      } catch (error) {
        buttonEl.disabled = true;
        initializedButtons.add(buttonEl);
        continue;
      }

      buttonEl.addEventListener('click', () => {
        // Hide toast if it's currently showing
        hideToast();

        // Only reset to 'fresh' if we're not in a "never show toast again" state
        const neverShowToastAgainStates: ToastSessionState[] = [
          'toastCompleted',
          'emailSubmitted',
        ];
        if (!neverShowToastAgainStates.includes(toastSessionState)) {
          toastSessionState = 'fresh';
        }

        buttonEl.disabled = true;
        show(loaderEl);

        // Create a new iframe every time
        let contentEl = modalEl!.getElementsByClassName('modal-content')[0];
        // Clear any existing iframe
        contentEl.innerHTML = '';

        let iframeEl: HTMLIFrameElement = createIframe();
        loadIframe(iframeEl, modalEl!, function () {
          hide(loaderEl);
          show(modalEl);
          buttonEl.disabled = false;
        });
      });

      // Mark this button as initialized
      initializedButtons.add(buttonEl);
    }

    // Only set up message listener if we have a valid host
    if (hasValidHost) {
      // receive message from child window
      window.addEventListener(
        'message',
        function (e: MessageEvent) {
          // Don't listen to messages from other origins
          if (e.origin !== new URL(host).origin) return;

          const data = 'message' in e && e.message ? e.message : e.data;
          if (data && data.action) {
            if (data.action === 'closeModal') {
              const activeIframe = modalEl!.querySelector('iframe');
              if (activeIframe) {
                lastClosedFormId = activeIframe.id;
              }
              hide(modalEl);
              iframeLoading = false;
            } else if (data.action === 'showPostCloseToast') {
              // Only show toast if we haven't already shown it in this session
              if (toastSessionState === 'fresh') {
                toastSessionState = 'toastShown';
                // Persist toast state and content
                embeddedSessionStorage.set('toastState', toastSessionState);
                embeddedSessionStorage.set('toastContent', data.content);
                embeddedSessionStorage.set('hostUrl', window.location.href);
                embeddedSessionStorage.set('lastUpdated', Date.now());
                showToast(data.content);
              }
            } else if (data.action === 'setToastSessionState') {
              toastSessionState = data.state;
              embeddedSessionStorage.set('toastState', data.state);
              embeddedSessionStorage.set('lastUpdated', Date.now());
            }
          }
        },
        false,
      );
    }
  }

  function createModalEl() {
    return createEl(
      'div',
      {
        className: 'embedded-pay-modal',
        innerHTML: "<div class='modal-content'></div>",
      },
      document.body,
    );
  }

  function createLoaderEl() {
    return createEl('div', { className: 'embedded-pay-loader' }, document.body);
  }

  function createToastEl() {
    return createEl(
      'div',
      {
        className: 'embedded-pay-toast',
        innerHTML: `
        <div class='toast-content'>
          <div class='toast-header'></div>
          <hr class='toast-divider' />
          <div class='toast-subtext'></div>
          <div class='toast-actions'>
            <button class='toast-complete-btn'></button>
            <button class='toast-dismiss-btn'></button>
          </div>
        </div>
      `,
      },
      document.body,
    );
  }

  function createIframe(): HTMLIFrameElement {
    const iframeId = `embedded-pay-embedded-widget-${Date.now()}`;
    return createEl<HTMLIFrameElement>(
      'iframe',
      {
        id: iframeId,
        style: { width: '100%', minHeight: '100%' },
        allow: 'payment',
        loading: 'eager',
      },
      null,
    );
  }

  function loadIframe(
    iframeEl: HTMLIFrameElement,
    modalEl: HTMLElement,
    callback: () => void,
  ) {
    if (iframeLoading) {
      return;
    }

    iframeLoading = true;
    show(loaderEl);

    // Check if we need to add restoreCartAbandonment parameter
    let iframeSrc = host;
    if (restoreCartAbandonment) {
      const url = new URL(host);
      url.searchParams.set('restoreCartAbandonment', 'true');
      iframeSrc = url.toString();

      // Reset the flag after using it
      restoreCartAbandonment = false;
    } else {
      // Always add restoreCartAbandonment parameter, defaulting to false
      const url = new URL(host);
      url.searchParams.set('restoreCartAbandonment', 'false');
      iframeSrc = url.toString();
    }

    // Set load event first before changing src
    iframeEl.onload = function () {
      hide(loaderEl);
      // Add a small delay to ensure loader is fully hidden before showing modal
      setTimeout(() => {
        callback();
        iframeEl.contentWindow?.focus();
      }, 50);
    };

    // Add to DOM before setting src for faster loading
    let contentEl = modalEl.getElementsByClassName('modal-content')[0];
    contentEl.appendChild(iframeEl);
    iframeEl.setAttribute('src', iframeSrc);
  }

  function show(el: HTMLElement | null) {
    if (el) {
      el.style.display = 'block';
      // Trigger reflow to ensure transition works
      el.offsetHeight;
      if (
        el.classList.contains('embedded-pay-modal') ||
        el.classList.contains('embedded-pay-toast')
      ) {
        el.classList.add('show');
        // Add body class to prevent scrolling when modal is shown
        if (el.classList.contains('embedded-pay-modal')) {
          document.body.classList.add('embedded-modal-open');
        }
      }
    }
  }

  function hide(el: HTMLElement | null) {
    if (el) {
      if (el.classList.contains('embedded-pay-modal')) {
        el.classList.remove('show');
        // Remove body class to restore scrolling
        document.body.classList.remove('embedded-modal-open');
        // Wait for transition to complete before hiding
        setTimeout(() => {
          el.style.display = 'none';
        }, 300);
      } else if (el.classList.contains('embedded-pay-toast')) {
        el.classList.remove('show');
        // Wait for slide-out transition to complete before hiding
        setTimeout(() => {
          el.style.display = 'none';
        }, 300);
      } else {
        el.style.display = 'none';
      }
    }
  }

  function showToast(content: ToastContent) {
    if (!toastEl) return;

    const headerEl = toastEl.querySelector('.toast-header');
    const subtextEl = toastEl.querySelector('.toast-subtext');
    const completeBtn: HTMLButtonElement | null = toastEl.querySelector(
      '.toast-complete-btn',
    );
    const dismissBtn: HTMLButtonElement | null =
      toastEl.querySelector('.toast-dismiss-btn');

    // Assign text sent from Plutus
    if (headerEl) {
      headerEl.textContent = content.heading;
    }
    if (subtextEl) {
      subtextEl.textContent = content.message;
    }
    if (completeBtn) {
      completeBtn.textContent = content.completeBtnText;
    }
    if (dismissBtn) {
      dismissBtn.textContent = content.dismissBtnText;
    }

    // Add complete donation button handler
    if (completeBtn) {
      completeBtn.onclick = () => {
        toastSessionState = 'toastCompleted';
        embeddedSessionStorage.set('toastState', toastSessionState);
        embeddedSessionStorage.set('lastUpdated', Date.now());

        if (lastClosedFormId) {
          const lastForm = document.getElementById(lastClosedFormId);
          if (lastForm) {
            // Find the modal by traversing up from the form
            const modalContent = lastForm.closest('.modal-content');
            if (modalContent) {
              const modal: HTMLElement | null = modalContent.closest(
                '.embedded-pay-modal',
              );
              if (modal) {
                show(modal);
                hideToast();
                return;
              }
            }
          }
        }

        // If we get here, there's no existing iframe - create new form with restore flag
        let donateButton: HTMLButtonElement | null = document.querySelector(
          '.embedded-pay-button',
        );
        if (donateButton) {
          // Add restore flag to the URL before clicking
          const currentHost = donateButton.getAttribute('data-host');
          if (currentHost) {
            restoreCartAbandonment = true;
          }
          donateButton.click();
        }
        hideToast();
      };
    }

    // Add dismiss button handler
    if (dismissBtn) {
      dismissBtn.onclick = () => {
        toastSessionState = 'toastDismissed';
        embeddedSessionStorage.set('toastState', toastSessionState);
        embeddedSessionStorage.set('lastUpdated', Date.now());
        hideToast();
      };
    }

    // Show toast with slide-in animation
    show(toastEl);
  }

  function hideToast() {
    if (toastEl) {
      hide(toastEl);
    }
  }

  function createEl<T extends HTMLElement = HTMLElement>(
    type: string,
    attributes: Record<string, any>,
    parent: HTMLElement | null,
  ): T {
    const el = document.createElement(type) as T;

    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'style' && typeof value === 'object') {
        Object.entries(value as Record<string, string>).forEach(
          ([styleKey, styleValue]) => {
            (el.style as any)[styleKey] = styleValue;
          },
        );
      } else {
        (el as any)[key] = value;
      }
    });

    if (parent) {
      parent.appendChild(el);
    }

    return el;
  }

  function setupMutationObserver() {
    // Watch for new buttons being added to the DOM
    const observer = new MutationObserver((mutations) => {
      let newButtonsFound = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;

            // Check if the added element is a button
            if (
              element.classList.contains('embedded-pay-button') &&
              !initializedButtons.has(element as HTMLButtonElement)
            ) {
              newButtonsFound = true;
            }

            // Check if the added element contains buttons
            const buttons = element.querySelectorAll('.embedded-pay-button');
            buttons.forEach((button) => {
              if (!initializedButtons.has(button as HTMLButtonElement)) {
                newButtonsFound = true;
              }
            });
          }
        });
      });

      if (newButtonsFound) {
        init();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Run immediately and also set up observer for dynamic content
  init();
  setupMutationObserver();
})(window, document);
